import type { ChangeEvent, ReactNode } from 'react';
import { DECKS } from '../../config/decks';
import { dailyTarget } from '../../domain/analytics/analytics';
import * as storage from '../../services/storage/storage';
import type { AppData, Card, DeckId } from '../../types';

interface Props {
  data: AppData;
  cards: Record<DeckId, Card[]>;
  onDataChanged: () => void;
}

export function SettingsView({ data, cards, onDataChanged }: Props) {
  function updateSettings(patch: Partial<AppData['settings']>) {
    Object.assign(storage.load().settings, patch);
    storage.save();
    onDataChanged();
  }

  function updateExamDate(deckId: DeckId, value: string) {
    storage.getDeckState(deckId).examDate = value || null;
    storage.save();
    onDataChanged();
  }

  function importFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    file.text().then((text) => {
      JSON.parse(text);
      storage.importAll(text);
      onDataChanged();
    }).catch(() => alert('Import fehlgeschlagen: Datei ist kein gültiger Klausurphase-Export.'));
  }

  function exportFile() {
    const blob = new Blob([storage.exportAll()], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `klausurphase-export-${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <section className="view active">
      <header className="page-head"><div><h1>Einstellungen</h1><p className="muted">Lernziele, Klausurmodus, Theme und Datenexport.</p></div></header>
      <div className="grid-2">
        <div className="panel settings-panel">
          <h2>Lernen</h2>
          <SettingRow title="Max. neue Karten pro Tag" sub="0 nutzt automatisch den Klausur-Zielwert."><input type="number" min={0} value={data.settings.maxNewPerDay} onChange={(event) => updateSettings({ maxNewPerDay: Number(event.target.value) || 0 })} /></SettingRow>
          <SettingRow title="Ziel-Retention" sub="Bestimmt die Intervall-Anpassung des SRS."><input type="number" min={0.5} max={0.99} step={0.01} value={data.settings.targetRetention} onChange={(event) => updateSettings({ targetRetention: Number(event.target.value) || 0.9 })} /></SettingRow>
          <SettingRow title="Schwache Kapitel priorisieren" sub="Kapitel mit niedriger Retention werden häufiger eingeplant."><input type="checkbox" checked={data.settings.boostWeak} onChange={(event) => updateSettings({ boostWeak: event.target.checked })} /></SettingRow>
          <SettingRow title="Klausurmodus" sub="Verdichtet Wiederholungsintervalle kurz vor Prüfungen."><input type="checkbox" checked={data.settings.examMode} onChange={(event) => updateSettings({ examMode: event.target.checked })} /></SettingRow>
          <SettingRow title="Theme" sub="Gilt lokal für dieses Gerät."><label className="select-wrap"><span className="sr-only">Theme</span><select className="select" value={data.settings.theme} onChange={(event) => updateSettings({ theme: event.target.value as 'light' | 'dark' })}><option value="light">☀️  Hell</option><option value="dark">🌙  Dunkel</option></select></label></SettingRow>
        </div>
        <div className="panel settings-panel">
          <h2>Klausurtermine</h2>
          <div className="exam-grid">
            {DECKS.map((deck) => {
              const deckState = data.decks[deck.id];
              const target = dailyTarget(cards[deck.id] ?? [], deckState?.cards ?? {}, deckState?.examDate ?? null, data.settings);
              return <div key={deck.id} className="exam-row"><div><div className="exam-name">{deck.icon} {deck.name}</div><div className="exam-sub">{deckState?.examDate ? `${target.daysUntilExam} Tage verbleibend` : 'Kein Termin gesetzt'}</div></div><input type="date" value={deckState?.examDate ?? ''} onChange={(event) => updateExamDate(deck.id, event.target.value)} /><div className="exam-target">{deckState?.examDate ? `${target.newPerDay}/Tag` : 'Automatisch'}</div></div>;
            })}
          </div>
        </div>
        <div className="panel settings-panel">
          <h2>Daten</h2>
          <p className="muted">Export enthält lokale SRS-Zustände, Verlauf, Einstellungen und Streak. Server-Sync läuft zusätzlich über deinen Account.</p>
          <div className="settings-actions"><button className="btn btn-primary" onClick={exportFile}>Exportieren</button><label className="btn-ghost import-label">Importieren<input type="file" accept="application/json" onChange={importFile} /></label><button className="btn-ghost danger" onClick={() => { if (confirm('Lokale Lerndaten wirklich zurücksetzen? Dies betrifft alle Fächer auf diesem Gerät.')) { storage.reset(); onDataChanged(); } }}>Zurücksetzen</button></div>
        </div>
      </div>
    </section>
  );
}

function SettingRow({ title, sub, children }: { title: string; sub: string; children: ReactNode }) {
  return <label className="setting-row"><span><span className="set-title">{title}</span><span className="set-sub">{sub}</span></span>{children}</label>;
}
